import React, {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useController, useFormContext } from "react-hook-form";
import { tv } from "tailwind-variants";

import type { ReactElement, ReactNode } from "react";
import type {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";

/**
 * Context to signal to child Rivet components that they're inside a Form.Item.
 * When true, child components should NOT display their own error messages
 * (Form.Item will handle error display).
 */
export const FormItemContext = createContext<boolean>(false);

/**
 * Hook for Rivet components to check if they're inside a Form.Item.
 * When inside Form.Item, components should skip their own error display.
 */
export function useFormItemContext(): boolean {
  return useContext(FormItemContext);
}

const formItem = tv({
  slots: {
    base: "flex flex-col gap-1",
    label: ["block", "text-sm", "font-semibold", "text-gray-900", "dark:text-gray-100"],
    content: "",
    helperWrapper: "flex flex-col gap-0.5",
    description: "text-xs text-gray-400",
    errorMessage: "text-xs text-red",
  },
  variants: {
    isRequired: {
      true: {
        label: "after:ml-0.5 after:text-red after:content-['*']",
      },
    },
    isInvalid: {
      true: {
        label: "!text-red",
      },
    },
    noStyle: {
      true: {
        base: "contents",
        label: "hidden",
        helperWrapper: "hidden",
      },
    },
  },
});

type ValueTransformer<TValue = unknown, TEvent = unknown> = (event: TEvent) => TValue;

type ValuePropsTransformer<TValue = unknown> = (value: TValue) => Record<string, unknown>;

export interface FormItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<UseControllerProps<TFieldValues, TName>, "control" | "rules"> {
  /** Field label displayed above the input */
  label?: ReactNode;
  /** Helper text displayed below the input */
  description?: ReactNode;
  /** Custom help text (alias for description, for antd compatibility) */
  help?: ReactNode;
  /** Whether the field is required (shows asterisk on label) */
  required?: boolean;
  /** Additional className for the wrapper */
  className?: string;
  /** Children to render (form input component) */
  children?: ReactNode;
  /**
   * Transform the event value before storing in form state.
   * Useful for custom input components that emit non-standard events.
   * @example getValueFromEvent={(e) => e.target.value}
   */
  getValueFromEvent?: ValueTransformer;
  /**
   * Transform the stored value before passing to the input component.
   * Returns an object of props to spread on the child.
   * @example getValueProps={(value) => ({ checked: value })}
   */
  getValueProps?: ValuePropsTransformer;
  /**
   * When true, don't render the label or wrapper, just the children
   * with form control props injected.
   */
  noStyle?: boolean;
  /**
   * Validation rules (for antd compatibility).
   * Note: Prefer using zod schema validation in the Form component.
   * @deprecated Use zod schema validation instead
   */
  rules?: Array<{
    required?: boolean;
    message?: string;
    validator?: (rule: unknown, value: unknown) => Promise<void>;
    warningOnly?: boolean;
  }>;
  /**
   * Fields that this field depends on for validation.
   * When these fields change, this field will be re-validated.
   */
  dependencies?: Array<string | string[]>;
}

/**
 * Internal component that renders when there's no form context.
 * Just renders children without form integration.
 */
function FormItemStatic({
  label,
  description,
  help,
  className,
  children,
  noStyle,
}: Pick<FormItemProps, "label" | "description" | "help" | "className" | "children" | "noStyle">) {
  const slots = useMemo(() => formItem({ noStyle }), [noStyle]);

  return (
    <div className={slots.base({ className })}>
      {label && <label className={slots.label()}>{label}</label>}
      <div className={slots.content()}>{children}</div>
      {(description || help) && (
        <div className={slots.helperWrapper()}>
          <span className={slots.description()}>{description || help}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Internal component that handles form integration.
 * Only used when there's a form context available.
 */
function FormItemConnected<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  description,
  help,
  required,
  className,
  children,
  getValueFromEvent,
  getValueProps,
  noStyle,
  rules,
  defaultValue,
  shouldUnregister,
}: FormItemProps<TFieldValues, TName>) {
  const { control, formState } = useFormContext<TFieldValues>();

  // Get nested error for array paths like ["user", "email"]
  const getNestedError = useCallback(
    (fieldName: TName) => {
      const parts = Array.isArray(fieldName) ? fieldName : String(fieldName).split(".");
      let current: unknown = formState.errors;
      for (const part of parts) {
        if (current && typeof current === "object" && part in current) {
          current = (current as Record<string, unknown>)[part];
        } else {
          return undefined;
        }
      }
      return current as { message?: string } | undefined;
    },
    [formState.errors]
  );

  const rulesConfig = useMemo(
    () =>
      rules?.length
        ? {
            validate: async (value: TFieldValues[TName]) => {
              // Run custom validators from rules
              for (const rule of rules) {
                if (rule.required && !value) {
                  if (!rule.warningOnly) {
                    return rule.message || "This field is required";
                  }
                }
                if (rule.validator) {
                  try {
                    await rule.validator(rule, value);
                  } catch (e) {
                    if (!rule.warningOnly) {
                      return e instanceof Error ? e.message : String(e);
                    }
                  }
                }
              }
              return true;
            },
          }
        : undefined,
    [rules]
  );

  const {
    field,
    fieldState: { error, invalid },
  } = useController<TFieldValues, TName>({
    name,
    control,
    defaultValue,
    shouldUnregister,
    rules: rulesConfig,
  });

  // Also check nested errors for array paths
  const nestedError = getNestedError(name);
  const errorMessage = error?.message || nestedError?.message;
  const isInvalid = invalid || !!nestedError;

  const helperText = description || help;

  const slots = useMemo(
    () =>
      formItem({
        isRequired: required,
        isInvalid,
        noStyle,
      }),
    [required, isInvalid, noStyle]
  );

  // Create the onChange handler that transforms the event value if needed
  const handleChange = useCallback(
    (eventOrValue: unknown) => {
      if (getValueFromEvent) {
        field.onChange(getValueFromEvent(eventOrValue));
      } else {
        // Try to extract value from common event shapes
        if (eventOrValue && typeof eventOrValue === "object" && "target" in eventOrValue) {
          const target = (eventOrValue as { target: HTMLInputElement }).target;
          // Use checked only for actual checkbox/radio inputs — all HTMLInputElements
          // have a "checked" property (defaults to false), so checking "checked" in target
          // would incorrectly match text inputs.
          if (
            (target.type === "checkbox" || target.type === "radio") &&
            typeof target.checked === "boolean"
          ) {
            field.onChange(target.checked);
          } else if ("value" in target) {
            field.onChange(target.value);
          } else {
            field.onChange(eventOrValue);
          }
        } else {
          field.onChange(eventOrValue);
        }
      }
    },
    [field, getValueFromEvent]
  );

  // Build props to pass to children
  // Note: We pass isInvalid for styling but NOT errorMessage - Form.Item handles error display
  // to avoid duplicate error messages when using Rivet components that have their own error slots
  const childProps = useMemo(() => {
    const baseProps: Partial<ControllerRenderProps<TFieldValues, TName>> & {
      isInvalid?: boolean;
      checked?: boolean;
      onCheckedChange?: (checked: boolean) => void;
    } = {
      name: field.name,
      onBlur: field.onBlur,
      ref: field.ref,
      disabled: field.disabled,
      isInvalid,
    };

    // Apply value transformation if provided
    if (getValueProps) {
      Object.assign(baseProps, getValueProps(field.value));
    } else {
      baseProps.value = field.value;
      // For boolean values, also pass as `checked` for Radix Switch/Checkbox compatibility
      if (typeof field.value === "boolean") {
        baseProps.checked = field.value;
      }
    }

    // Use transformed onChange
    baseProps.onChange = handleChange;
    // Also pass onCheckedChange for Radix Switch/Checkbox compatibility —
    // these components use onCheckedChange(boolean) instead of onChange(event)
    baseProps.onCheckedChange = handleChange;

    return baseProps;
  }, [field, isInvalid, getValueProps, handleChange]);

  // Clone children and inject form props
  const enhancedChildren = useMemo(() => {
    if (!isValidElement(children)) {
      return children;
    }

    return cloneElement(children as ReactElement<Record<string, unknown>>, childProps);
  }, [children, childProps]);

  if (noStyle) {
    return <FormItemContext.Provider value>{enhancedChildren}</FormItemContext.Provider>;
  }

  return (
    <FormItemContext.Provider value>
      <div className={slots.base({ className })}>
        {label && <label className={slots.label()}>{label}</label>}
        <div className={slots.content()}>{enhancedChildren}</div>
        {(helperText || errorMessage) && (
          <div className={slots.helperWrapper()}>
            {errorMessage ? (
              <span className={slots.errorMessage()}>{errorMessage}</span>
            ) : helperText ? (
              <span className={slots.description()}>{helperText}</span>
            ) : null}
          </div>
        )}
      </div>
    </FormItemContext.Provider>
  );
}

/**
 * Form.Item provides a wrapper for form fields that:
 * - Displays a label above the input
 * - Shows validation errors from the form context
 * - Connects children to react-hook-form via useController
 *
 * @example
 * ```tsx
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <Form.Item name="email" label="Email" required>
 *     <Input placeholder="Enter email" />
 *   </Form.Item>
 * </Form>
 * ```
 */
export function FormItem<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: FormItemProps<TFieldValues, TName>) {
  const formContext = useFormContext<TFieldValues>();

  // If no form context, just render children without form integration
  if (!formContext) {
    return (
      <FormItemStatic
        label={props.label}
        description={props.description}
        help={props.help}
        className={props.className}
        noStyle={props.noStyle}
      >
        {props.children}
      </FormItemStatic>
    );
  }

  return <FormItemConnected {...props} />;
}

FormItem.displayName = "Form.Item";
